import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Form, Formik } from "formik";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import * as Yup from "yup";

import ErrorCard from "../ErrorCard";
import { FlexBox } from "../FlexBox";
import Loading from "../Loading";
import { FormikTextField } from "../forms/FormTextField";
import FormikDateTimePicker from "../forms/FormikDateTimePicker";
import { FormikSubmitButton } from "../forms/SubmitButton";
import { useOptOutList } from "./useOptOutList";

export const EditAdminList = ({
  listId,
  onClose,
}: {
  listId: string;
  onClose: () => void;
}): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const { list, loading, error, updateList } = useOptOutList(listId);

  if (loading || !list) {
    return <Loading />;
  }
  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <>
      <DialogTitle>Edit {list.name}</DialogTitle>
      <Formik
        initialValues={{
          name: list.name,
          type: list.type,
          expiresAt:
            list.type === "TEMPORARY"
              ? DateTime.fromMillis(list.expiresAt)
              : DateTime.now(),
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Required").min(1),
          type: Yup.string().required("Required"),
          expiresAt: Yup.date()
            .optional()
            .when("type", {
              is: "TEMPORARY",
              then: Yup.date()
                .min(new Date(), "Expiration date must be in the future")
                .required("Required"),
            }),
        })}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const { errors } = await updateList({
            variables: {
              input: {
                listId: list.id,
                name: values.name,
                expiresAt: list.type === "TEMPORARY" ? values.expiresAt : null,
              },
            },
          });

          if (errors) {
            setSubmitting(false);
          } else {
            enqueueSnackbar("List updated", { variant: "success" });
            resetForm();
            onClose();
          }
        }}
      >
        {({ isValid, isSubmitting, submitForm }) => (
          <Form>
            <DialogContent>
              <FlexBox direction="column">
                <FormikTextField name="name" label="List Name" />

                {list.type === "TEMPORARY" && (
                  <FormikDateTimePicker
                    fullWidth
                    minDate={DateTime.now()}
                    minDateError="Expiration date must be in the future"
                    name="expiresAt"
                    label="List Expiration Date"
                    helperText="Contacts on this list won't be messaged until after this date"
                  />
                )}
              </FlexBox>
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose}>
                Cancel
              </Button>
              <FormikSubmitButton
                disabled={!isValid}
                onClick={submitForm}
                submitText="Save changes"
                loadingText="Saving..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </>
  );
};
